import { Injectable, OnDestroy } from '@angular/core';
import { CompaniesService } from './companies.service';
import { DEFAULT_TIMEZONE_NAME } from '@constants/constants';
import { BehaviorSubject, ReplaySubject, throwError } from 'rxjs';
import { DateHelper } from '@helpers/date.helper';
import { BusinessIntelligenceGroupingEnum } from '@data-layer/enums/common';
import { takeUntil } from 'rxjs/operators';

import dayjs from '../services/dayjs.service';

@Injectable({
  providedIn: 'root',
})
export class DateTimezoneService implements OnDestroy {
  public timezone: BehaviorSubject<string> = new BehaviorSubject<string>(DEFAULT_TIMEZONE_NAME);

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private companiesService: CompaniesService) {
    this.companiesService.userChange$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.timezone.next(user.current && user.current.timeZone ? user.current.timeZone : DEFAULT_TIMEZONE_NAME);
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public newTimezoneDate(date: {
    year: number;
    month: number;
    day: number;
    hour?: number;
    minute?: number;
    second?: number;
    millisecond?: number;
  }): string {
    if (date) {
      // Look on the issue in Day.js repository. Waiting resolving issue and then remove @ts-ignore.
      // https://github.com/iamkun/dayjs/pull/1647
      // @ts-ignore
      return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).toISOString();
    } else {
      throwError('Invalid date format');
    }
  }

  public formatInUserTZ(date: string | Date, format?: string): string {
    return dayjs(date).tz(this.timezone.getValue()).format(format);
  }

  public format(date: string | Date, format?: string): string {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).format(format);
  }

  public formatInUTC(date: string, format?: string): string {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).format(format);
  }

  public formatLocalToTz(date: string, format?: string): string {
    return dayjs.tz(date.split('+')[0], DEFAULT_TIMEZONE_NAME).format(format);
  }

  public mergeDateAndTime(date: string, time: string): string {
    const _date: dayjs.Dayjs = dayjs.tz(date, DEFAULT_TIMEZONE_NAME);
    const _time: dayjs.Dayjs = dayjs.tz(time, DEFAULT_TIMEZONE_NAME);

    return DateHelper.newUtcDate({
      year: _date.year(),
      month: _date.month(),
      day: _date.date(),
      hour: _time.hour(),
      minute: _time.minute(),
      second: _time.second(),
      millisecond: _time.millisecond(),
    });
  }

  public daysBetween(fromDate: string, toDate: string, floating: boolean = false): number {
    const _fromDate: dayjs.Dayjs = dayjs(fromDate).tz(DEFAULT_TIMEZONE_NAME);
    const _toDate: dayjs.Dayjs = dayjs(toDate).tz(DEFAULT_TIMEZONE_NAME);
    return dayjs(_toDate).diff(_fromDate, 'days', floating);
  }

  public weeksBetween(fromDate: string, toDate: string): number {
    const _fromDate: dayjs.Dayjs = dayjs(fromDate).tz(DEFAULT_TIMEZONE_NAME);
    const _toDate: dayjs.Dayjs = dayjs(toDate).tz(DEFAULT_TIMEZONE_NAME);
    return dayjs(_toDate).diff(_fromDate, 'week');
  }

  public monthsBetween(fromDate: string, toDate: string): number {
    const _fromDate: dayjs.Dayjs = dayjs(fromDate).tz(DEFAULT_TIMEZONE_NAME);
    const _toDate: dayjs.Dayjs = dayjs(toDate).tz(DEFAULT_TIMEZONE_NAME);
    return dayjs(_toDate).diff(_fromDate, 'month');
  }

  public yearsBetween(fromDate: string, toDate: string): number {
    const _fromDate: dayjs.Dayjs = dayjs(fromDate).tz(DEFAULT_TIMEZONE_NAME);
    const _toDate: dayjs.Dayjs = dayjs(toDate).tz(DEFAULT_TIMEZONE_NAME);
    return dayjs(_toDate).diff(_fromDate, 'year');
  }

  public startOfTheDay(date: string): string {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).startOf('day').toISOString();
  }

  public endOfTheDay(date: string): string {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).endOf('day').toISOString();
  }

  public startOfTheMonth(date: string): string {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).startOf('month').toISOString();
  }

  public endOfTheMonth(date: string): string {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).endOf('month').toISOString();
  }

  public endOfThePreviousMonth(date: string): string {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).subtract(1, 'months').endOf('month').toISOString();
  }

  public startOfThePreviousMonth(date: string): string {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).subtract(1, 'month').startOf('month').toISOString();
  }

  public endOfTheNextMonth(date: string): string {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).subtract(-1, 'months').endOf('month').toISOString();
  }

  public startOfTheNextMonth(date: string): string {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).subtract(-1, 'months').startOf('month').toISOString();
  }

  public startOfTheQuarter(date: string): string {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).startOf('quarter').toISOString();
  }

  public endOfTheQuarter(date: string): string {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).endOf('quarter').toISOString();
  }

  public startOfTheYear(date: string): string {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).startOf('year').toISOString();
  }

  public endOfTheYear(date: string): string {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).endOf('year').toISOString();
  }

  public addHours(date: string, hours: number): string {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).add(hours, 'hour').toISOString();
  }

  public addDays(date: string, days: number): string {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).add(days, 'day').toISOString();
  }

  public addWeeks(date: string, weeks: number): string {
    return dayjs(date).add(weeks, 'week').toISOString();
  }

  public addMonths(date: string, months: number): string {
    return dayjs(date).add(months, 'month').toISOString();
  }

  public addQuarter(date: string, quarter: number): string {
    return dayjs(date).add(quarter, 'quarter').toISOString();
  }

  public setQuarterStart(date: string, quarter: number): string {
    return dayjs().quarter(quarter).startOf('quarter').toISOString();
  }

  public setQuarterEnd(date: string, quarter: number): string {
    return dayjs().quarter(quarter).endOf('quarter').toISOString();
  }

  public isAfterQuarterEnd(quarter: number): boolean {
    const endOfQuarterDay: dayjs.Dayjs = dayjs().quarter(quarter).endOf('day');
    const endOfQuarter: dayjs.Dayjs = dayjs().endOf('quarter');

    return dayjs(endOfQuarter).isSameOrBefore(endOfQuarterDay);
  }

  public addYears(date: string, years: number): string {
    return dayjs(date).add(years, 'year').toISOString();
  }

  public dayOfWeek(date: string): number {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).weekday();
  }

  public year(date: string): number {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).year();
  }

  public month(date: string): number {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).month();
  }

  public date(date: string): number {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).date();
  }

  public hour(date: string): number {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).hour();
  }

  public minute(date: string): number {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).minute();
  }

  public second(date: string): number {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).second();
  }

  public getUserTimezoneDate(date: string): string {
    return dayjs(date).tz(DEFAULT_TIMEZONE_NAME).toISOString();
  }

  public static formatInTimezone(date: string, timezoneName: string, format: string): string {
    return dayjs(date).tz(timezoneName).format(format);
  }

  public formatModelValue(value: string, useSeconds: boolean = true): Date {
    if (!value) {
      return null;
    }

    return new Date(
      DateHelper.yearByUtc(value),
      DateHelper.monthByUtc(value),
      DateHelper.dateByUtc(value),
      DateHelper.hourByUtc(value),
      DateHelper.minuteByUtc(value),
      useSeconds ? DateHelper.secondByUtc(value) : 0,
    );
  }

  public formatViewValue(value: Date, useSeconds: boolean = true): string {
    if (value) {
      return DateHelper.newUtcDate({
        year: value.getFullYear(),
        month: value.getMonth(),
        day: value.getDate(),
        hour: value.getHours(),
        minute: value.getMinutes(),
        second: useSeconds ? value.getSeconds() : 0,
      });
    }
    return null;
  }

  public getPeriodValue(minDate: string, maxDate: string): BusinessIntelligenceGroupingEnum {
    if (this.weeksBetween(minDate, maxDate) <= 1) {
      return BusinessIntelligenceGroupingEnum.DAY;
    } else if (this.monthsBetween(minDate, maxDate) <= 1) {
      return BusinessIntelligenceGroupingEnum.WEEK;
    } else if (this.yearsBetween(minDate, maxDate) <= 1) {
      return BusinessIntelligenceGroupingEnum.MONTH;
    } else {
      return BusinessIntelligenceGroupingEnum.YEAR;
    }
  }

  public getTimeZoneByName(timeZoneName: string): string {
    return timeZoneName ? dayjs().tz(timeZoneName).format('Z') : null;
  }

  public formatDateWithoutTime(value: string, isUSFormat: boolean = true): string {
    try {
      if (!!value?.match(/^(\d{4}\-\d\d\-\d\d([tT][\d:\.]*)?)([zZ]|([+\-])(\d\d):?(\d\d))?$/)) {
        const monthVal: string = value.substring(5, 7);
        const dayVal: string = value.substring(8, 10);
        const yearVal: string = value.substring(0, 4);

        return isUSFormat ? `${monthVal}/${dayVal}/${yearVal}` : `${yearVal}-${monthVal}-${dayVal}`;
      } else {
        return null;
      }
    } catch (e) {
      return null;
    }
  }

  public formatViewValueWithRemovalOfZones(input: string, input2: string, input3: string): string {
    try {
      const formattedDate: string = new Date(input).toISOString();
      const formattedDateWithoutTime: string = this.formatDateWithoutTime(input, false);

      // if date formatted and date of input are equal -> else split timezone
      if (!!formattedDateWithoutTime) {
        if (formattedDateWithoutTime === formattedDate.substring(0, 10)) {
          return input;
        } else {
          return `${formattedDateWithoutTime}T00:00:00Z`;
        }
      } else {
        return input;
      }
    } catch (e) {
      return input;
    }
  }
}
